import * as React from 'react';

import {
  Button,
  Col,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Row,
} from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import {
  LucideCheck,
  LucideCopy,
  LucideLink,
  LucideShare2,
} from 'lucide-react';

import { $shareChat, $unshareChat } from '../../_state/chatConversation.state';
import { useTranslations } from '../../i18n/useTranslations';

interface ShareDialogProps {
  chatId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

//
// ⚛️ ⚛️ ⚛️ SHARE LINK DISPLAY ⚛️ ⚛️ ⚛️
//
interface ShareLinkDisplayProps {
  shareUrl: string;
  isCopied: boolean;
  onCopy: () => void;
  copiedLabel: string;
  copyLinkLabel: string;
}

const ShareLinkDisplay = ({
  shareUrl,
  isCopied,
  onCopy,
  copiedLabel,
  copyLinkLabel,
}: ShareLinkDisplayProps) => (
  <Row
    gap2
    className="@ShareLinkDisplay rounded-md border border-neutral/20 bg-neutral/5 px-3 py-2"
  >
    <LucideLink className="size-4 shrink-0 text-neutral/50" />
    <span className="min-w-0 flex-1 truncate text-sm">{shareUrl}</span>
    <Button
      size="small"
      variant={isCopied ? 'solid' : 'outline'}
      onPress={onCopy}
      className="w-28 max-w-28 transition-colors"
      icon={isCopied ? <LucideCheck size={14} /> : <LucideCopy size={14} />}
    >
      {isCopied ? copiedLabel : copyLinkLabel}
    </Button>
  </Row>
);

//
// ⚛️ ⚛️ ⚛️ REVOKE CONFIRM FOOTER ⚛️ ⚛️ ⚛️
//
interface RevokeConfirmFooterProps {
  isRevoking: boolean;
  onCancel: () => void;
  onRevoke: () => void;
  revokeConfirmLabel: string;
  cancelLabel: string;
  revokeLabel: string;
}

const RevokeConfirmFooter = ({
  isRevoking,
  onCancel,
  onRevoke,
  revokeConfirmLabel,
  cancelLabel,
  revokeLabel,
}: RevokeConfirmFooterProps) => (
  <Row yCenter xBetween gap2 className="@RevokeConfirmFooter w-full">
    <p className="mr-auto text-sm text-failure">{revokeConfirmLabel}</p>
    <Button variant="outline" onPress={onCancel} isDisabled={isRevoking}>
      {cancelLabel}
    </Button>
    <Button
      variant="solid"
      color="failure"
      onPress={onRevoke}
      isDisabled={isRevoking}
      isLoading={isRevoking}
    >
      {revokeLabel}
    </Button>
  </Row>
);

//
// ⚛️ ⚛️ ⚛️ DEFAULT FOOTER ⚛️ ⚛️ ⚛️
//
interface DefaultFooterProps {
  isLoading: boolean;
  token: string | null;
  onRevokeClick: () => void;
  onClose: () => void;
  cancelLabel: string;
  revokeLabel: string;
}

const DefaultFooter = ({
  isLoading,
  token,
  onRevokeClick,
  onClose,
  cancelLabel,
  revokeLabel,
}: DefaultFooterProps) => (
  <>
    <Button
      variant="outline"
      color="neutral"
      onPress={onRevokeClick}
      isDisabled={isLoading || !token}
    >
      {revokeLabel}
    </Button>
    <Button variant="solid" onPress={onClose}>
      {cancelLabel}
    </Button>
  </>
);

//
// ⚛️ ⚛️ ⚛️ SHARE DIALOG ⚛️ ⚛️ ⚛️
//
export const ShareDialog = ({
  chatId,
  isOpen,
  onOpenChange,
}: ShareDialogProps) => {
  const t = useTranslations();

  // States
  const [token, setToken] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState(false);
  const [isRevoking, setIsRevoking] = React.useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = React.useState(false);

  // Atoms
  const shareChat = useSetAtom($shareChat);
  const unshareChat = useSetAtom($unshareChat);

  // Memoized values
  const shareUrl = React.useMemo(
    () => (token ? `${window.location.origin}/assistant/share/${token}` : ''),
    [token],
  );

  // Callbacks
  const handleCopy = React.useCallback(async () => {
    await navigator.clipboard.writeText(shareUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }, [shareUrl]);

  const handleRevoke = React.useCallback(async () => {
    setIsRevoking(true);
    try {
      await unshareChat({ chatId });
      onOpenChange(false);
    } finally {
      setIsRevoking(false);
    }
  }, [unshareChat, chatId, onOpenChange]);

  /** Shares the chat when dialog opens, resets state when it closes. */
  React.useEffect(() => {
    if (!isOpen) {
      setToken(null);
      setIsCopied(false);
      setShowRevokeConfirm(false);
      setIsRevoking(false);
      return;
    }

    setIsLoading(true);
    shareChat({ chatId })
      .then((result) => {
        if (result) {
          setToken(result.token);
        }
      })
      .finally(() => setIsLoading(false));
  }, [isOpen, chatId, shareChat]);

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@ShareDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <LucideShare2 className="size-5" />
            {t.header.shareDialog.title}
          </DialogTitle>
        </DialogHeader>
        <Col gap3 className="p-4">
          <p className="text-sm text-neutral/70">
            {t.header.shareDialog.description}
          </p>
          {isLoading && (
            <Row xCenter className="py-4">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral/20 border-t-primary" />
            </Row>
          )}
          {!isLoading && token && (
            <ShareLinkDisplay
              shareUrl={shareUrl}
              isCopied={isCopied}
              onCopy={handleCopy}
              copiedLabel={t.header.shareDialog.copied}
              copyLinkLabel={t.header.shareDialog.copyLink}
            />
          )}
        </Col>

        <DialogFooter>
          {showRevokeConfirm ? (
            <RevokeConfirmFooter
              isRevoking={isRevoking}
              onCancel={() => setShowRevokeConfirm(false)}
              onRevoke={handleRevoke}
              cancelLabel={t.header.shareDialog.cancel}
              revokeLabel={t.header.shareDialog.revoke}
              revokeConfirmLabel={t.header.shareDialog.revokeConfirm}
            />
          ) : (
            <DefaultFooter
              isLoading={isLoading}
              token={token}
              onRevokeClick={() => setShowRevokeConfirm(true)}
              onClose={() => onOpenChange(false)}
              cancelLabel={t.header.shareDialog.cancel}
              revokeLabel={t.header.shareDialog.revoke}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
